/**
 * `/settings/admin` — *All teams* table (#2058, master account slice B1).
 *
 * Lists every team on the platform from `GET /api/admin/teams`, which
 * reads `teams` directly — so a team the operator is not a member of is
 * listed too. Search + sort are client-side (the list is small: one row
 * per team). Each row has an *Open* link to the team's home; a team the
 * operator is not a member of opens read-only (the observer scope, #2059;
 * the shell shows the read-only chip, #2060).
 *
 * Rendered by `AdminPage` only once the admin gate has passed; a 404
 * here is treated the same way (the section stays hidden). #2060 — also
 * the master account's Home (an admin account with no team of its own),
 * with each team's week status (`withStatus`).
 */
import { useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, apiFetch } from "../../api/client";
import { type AdminAllTeamsRow, type AdminLeadRef, useAdminTeams } from "../../api/useAdminTeams";
import {
  Badge,
  Button,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
  EmptyState,
  Input,
  Skeleton,
} from "../../components/ui";
import { useAuth } from "../../lib/AuthContext";
import { cn } from "../../lib/cn";
import { todayIso } from "../../lib/rosterDatesWindow";
import { type TeamKind, resolveTeamHomeRoute } from "../../lib/teamApps";
import {
  type HomeCycle,
  weekLabel,
  weekOf,
  weekStatus,
  weekStatusTone,
  weekStatusWords,
} from "../team/teamHomeFacts";
import { MixupFlag } from "./admin/AdminTeamTile";
import { ScrollFrame } from "./admin/ScrollFrame";
import { type SortDirection, SortableHeader } from "./admin/SortableHeader";
import { LabelMark, PriorityMark } from "./admin/TeamMarks";
import { ownerLabel, personLabel, spanWords, splitTeamName } from "./admin/adminWords";
import { memberSplit, memberWords } from "./admin/memberWords";
import type { TeamFlag } from "./admin/teamFlags";
import { teamKicker, teamKindLabel } from "./admin/teamKind";
import {
  type OrderContext,
  type TeamGroup,
  type TeamsSort,
  groupTeams,
  matchesLabel,
} from "./admin/teamOrder";

type SortKey = "name" | "members" | "last_solve";

/** People on the roster: seats with an account plus placeholders. */
function memberTotal(row: AdminAllTeamsRow): number {
  return row.member_counts.has_account + row.member_counts.placeholder;
}

function teamHome(row: AdminAllTeamsRow): string {
  const kind: TeamKind = row.team_kind === "tasks" ? "tasks" : "scheduling";
  return resolveTeamHomeRoute(kind, row.team_id);
}

const dateTimeFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : dateTimeFormat.format(d);
}

function compareRows(a: AdminAllTeamsRow, b: AdminAllTeamsRow, key: SortKey): number {
  if (key === "members") return memberTotal(a) - memberTotal(b);
  if (key === "last_solve") {
    // Never-solved teams sort as the oldest.
    const at = a.last_solve_at ? Date.parse(a.last_solve_at) : 0;
    const bt = b.last_solve_at ? Date.parse(b.last_solve_at) : 0;
    return at - bt;
  }
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

/** The teams a search and a check chip leave, by name. Shared with the tile view. */
export function filterTeams(
  teams: readonly AdminAllTeamsRow[],
  search: string,
  idFilter: ReadonlySet<string> | null,
  label: string | null = null,
): AdminAllTeamsRow[] {
  const all = teams.filter(
    (r) => (!idFilter || idFilter.has(r.team_id)) && matchesLabel(r.admin_label, label),
  );
  const needle = search.trim().toLowerCase();
  const filtered = needle
    ? all.filter(
        (row) =>
          row.name.toLowerCase().includes(needle) ||
          (row.owner_email ?? "").toLowerCase().includes(needle) ||
          row.team_id.toLowerCase().startsWith(needle),
      )
    : [...all];
  return filtered.sort((a, b) => compareRows(a, b, "name"));
}

const DEFAULT_DIRECTION: Record<SortKey, SortDirection> = {
  name: "asc",
  members: "desc",
  last_solve: "desc",
};

/** #2825 — the admin console's Teams tab: it brings its own search box,
 *  adds a Leads column, filters to a check's rows and opens a drawer on a
 *  row click. */
export interface AdminConsoleTeamsMode {
  search: string;
  /** Only these team ids (a check chip); null = all. */
  idFilter: ReadonlySet<string> | null;
  selectedId: string | null;
  onOpenTeam: (teamId: string) => void;
  /** Named slot: extra columns for the later slices (adoption, setup, last
   *  change). With none passed, the Ledger table shows none. */
  extraColumns?: readonly AdminTableColumn[];
  /** Mix-ups the checks found, by team id. */
  flags?: ReadonlyMap<string, readonly TeamFlag[]>;
  /** #2872 — the page order: group heads, and a header click sorts inside each group. */
  order?: { mode: TeamsSort; ctx: OrderContext; label: string | null };
}

export interface AdminTableColumn {
  key: string;
  header: React.ReactNode;
  cell: (row: AdminAllTeamsRow) => React.ReactNode;
}

export interface AdminAllTeamsTableProps {
  console?: AdminConsoleTeamsMode;
  /** #2060 — a *This week* column: each team's week status, read lazily
   *  (one request per row, once the row scrolls into view). */
  withStatus?: boolean;
  /** Overrides the section heading's description line. */
  description?: string;
}

export function AdminAllTeamsTable({
  withStatus = false,
  description,
  console: consoleMode,
}: AdminAllTeamsTableProps) {
  const [ownSearch, setSearch] = useState<string>("");
  const search = consoleMode ? consoleMode.search : ownSearch;
  const idFilter = consoleMode?.idFilter ?? null;
  const [sortKey, setSortKey] = useState<SortKey | null>(consoleMode?.order ? null : "name");
  const [direction, setDirection] = useState<SortDirection>("asc");

  const teams = useAdminTeams();

  const order = consoleMode?.order;
  // Sort by wins after a change: a header sort holds only until the mode changes.
  const orderMode = order?.mode;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the mode is the trigger
  useEffect(() => {
    if (orderMode) setSortKey(null);
  }, [orderMode]);
  const groups = useMemo<TeamGroup[]>(() => {
    const filtered = filterTeams(teams.data?.teams ?? [], search, idFilter, order?.label ?? null);
    const sign = direction === "asc" ? 1 : -1;
    const inner = sortKey
      ? (a: AdminAllTeamsRow, b: AdminAllTeamsRow) => sign * compareRows(a, b, sortKey)
      : undefined;
    if (order) return groupTeams(filtered, order.mode, order.ctx, inner);
    return [
      {
        key: "all",
        title: null,
        rows: [...filtered].sort(inner ?? ((a, b) => compareRows(a, b, "name"))),
      },
    ];
  }, [teams.data, search, sortKey, direction, idFilter, order]);
  const rows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  function onSort(key: SortKey) {
    if (key === sortKey) {
      setDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setDirection(DEFAULT_DIRECTION[key]);
    }
  }

  // A 404 means the gate refused — nothing to show (AdminPage already
  // renders its own not-found state for that case).
  if (teams.error instanceof ApiError && teams.error.status === 404) return null;

  const total = teams.data?.teams.length ?? 0;

  return (
    <section className="flex min-w-0 flex-col gap-3" data-testid="admin-all-teams">
      {consoleMode ? null : (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold text-fg">All teams</h2>
            <p className="text-xs text-fg-muted">
              {description ??
                "Every team on Tarii, whether or not you are a member. A team you are not on opens read only."}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="admin-teams-search" className="text-xs text-fg-muted">
              Search by team name or owner email
            </label>
            <Input
              id="admin-teams-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ward 7"
              data-testid="admin-all-teams-search"
            />
          </div>
        </div>
      )}

      {teams.isLoading ? <Skeleton className="h-24" data-testid="admin-all-teams-loading" /> : null}

      {teams.error ? (
        <EmptyState title="Couldn't load teams" description={teams.error.detail} />
      ) : null}

      {teams.data ? (
        <>
          <p className="text-xs text-fg-faint" data-testid="admin-all-teams-count">
            {rows.length === total ? `${total} teams` : `${rows.length} of ${total} teams`}
            {consoleMode ? "" : " · members read with an account / placeholder / invited"}
          </p>
          <Frame framed={Boolean(consoleMode)}>
            <DataTable
              data-testid="admin-all-teams-table"
              scroll={!consoleMode}
              className={consoleMode ? CONSOLE_TABLE : undefined}
            >
              <DataTableHead>
                <DataTableRow>
                  <SortableHeader
                    testidPrefix="admin-all-teams-sort"
                    label="Team"
                    active={sortKey === "name"}
                    direction={direction}
                    onClick={() => onSort("name")}
                  />
                  {consoleMode ? (
                    <>
                      <DataTableHeader>Priority</DataTableHeader>
                      <DataTableHeader>Label</DataTableHeader>
                    </>
                  ) : null}
                  {withStatus ? <DataTableHeader>This week</DataTableHeader> : null}
                  {consoleMode ? null : <DataTableHeader>Kind</DataTableHeader>}
                  <DataTableHeader>Owner</DataTableHeader>
                  {consoleMode ? <DataTableHeader>Lead</DataTableHeader> : null}
                  <SortableHeader
                    testidPrefix="admin-all-teams-sort"
                    label="Members"
                    active={sortKey === "members"}
                    direction={direction}
                    onClick={() => onSort("members")}
                  />
                  {consoleMode ? (
                    <DataTableHeader>Latest roster</DataTableHeader>
                  ) : (
                    <>
                      <DataTableHeader>Rosters</DataTableHeader>
                      <DataTableHeader>Published through</DataTableHeader>
                      <SortableHeader
                        testidPrefix="admin-all-teams-sort"
                        label="Last solve"
                        active={sortKey === "last_solve"}
                        direction={direction}
                        onClick={() => onSort("last_solve")}
                      />
                      <DataTableHeader>Plan</DataTableHeader>
                      <DataTableHeader>
                        <span className="sr-only">Actions</span>
                      </DataTableHeader>
                    </>
                  )}
                  {consoleMode?.extraColumns?.map((col) => (
                    <DataTableHeader key={col.key}>{col.header}</DataTableHeader>
                  ))}
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {groups.map((group) => (
                  <Fragment key={group.key}>
                    {group.title ? (
                      <DataTableRow noZebra data-testid={`admin-all-teams-group-${group.key}`}>
                        <DataTableCell
                          colSpan={
                            5 + (consoleMode ? 2 : 0) + (consoleMode?.extraColumns?.length ?? 0)
                          }
                          className="pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-fg-muted"
                        >
                          <span className="sticky left-2 inline-block">
                            {group.title} <span className="font-normal">{group.rows.length}</span>
                          </span>
                        </DataTableCell>
                      </DataTableRow>
                    ) : null}
                    {group.rows.map((row) =>
                      consoleMode ? (
                        <ConsoleTeamRow key={row.team_id} row={row} consoleMode={consoleMode} />
                      ) : (
                        <TeamRow key={row.team_id} row={row} withStatus={withStatus} />
                      ),
                    )}
                  </Fragment>
                ))}
              </DataTableBody>
            </DataTable>
          </Frame>
          {rows.length === 0 ? (
            <p className="text-xs text-fg-muted" data-testid="admin-all-teams-empty">
              No teams match.
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function Frame({ framed, children }: { framed: boolean; children: React.ReactNode }) {
  return framed ? <ScrollFrame label="Teams table">{children}</ScrollFrame> : <>{children}</>;
}

/** #2872 — tight cell padding so every column fits a 1440 px laptop. */
export const CONSOLE_TABLE = "[&_td]:px-2 [&_th]:px-2";

const faint = <span className="text-fg-faint">—</span>;

/** The console's Lead cell: the lead names, and a flag when nobody can lead. */
function LeadsCell({ leads, noLead }: { leads: AdminLeadRef[]; noLead: boolean }) {
  const shown = leads.slice(0, 2);
  return (
    <div className="flex min-w-[7rem] flex-col gap-0.5">
      {shown.map((lead) => (
        <span key={lead.account_id} className="flex flex-wrap items-center gap-1">
          <span className="break-words text-fg" title={lead.email}>
            {personLabel(lead)}
          </span>
          {lead.role === "co_lead" ? <span className="text-xs text-fg-faint">co-lead</span> : null}
          {lead.has_login ? null : <MixupFlag>No login</MixupFlag>}
        </span>
      ))}
      {leads.length > shown.length ? (
        <span className="text-xs text-fg-faint">+{leads.length - shown.length} more</span>
      ) : null}
      {noLead ? (
        <span>
          <MixupFlag>No lead</MixupFlag>
        </span>
      ) : null}
    </div>
  );
}

/** One row of the console's Ledger table: hairline, tabular, opens the drawer. */
function ConsoleTeamRow({
  row,
  consoleMode,
}: {
  row: AdminAllTeamsRow;
  consoleMode: AdminConsoleTeamsMode;
}) {
  const flags = consoleMode.flags?.get(row.team_id) ?? [];
  const kicker = teamKicker(row);
  const leads = row.leads ?? [];
  const { org, team } = splitTeamName(row.name);
  return (
    <DataTableRow
      noZebra
      data-testid={`admin-all-teams-row-${row.team_id}`}
      onClick={() => consoleMode.onOpenTeam(row.team_id)}
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          consoleMode.onOpenTeam(row.team_id);
        }
      }}
      className={cn(
        "cursor-pointer text-sm hover:bg-bg-subtle focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
        consoleMode.selectedId === row.team_id && "bg-accent-soft/40",
      )}
    >
      <DataTableCell className="py-2.5">
        <div className="flex min-w-[9rem] flex-wrap items-baseline gap-x-2">
          {kicker ? <span className="text-xs text-fg-faint">{kicker}</span> : null}
          {/* #2060 — the name opens the team too. */}
          <div className="flex min-w-0 flex-col">
            {org ? (
              <span
                className="text-xs text-fg-muted"
                data-testid={`admin-all-teams-org-${row.team_id}`}
              >
                {org}
              </span>
            ) : null}
            <Link
              to={teamHome(row)}
              onClick={(e) => e.stopPropagation()}
              className="break-words font-semibold text-fg hover:underline"
              data-testid={`admin-all-teams-name-${row.team_id}`}
            >
              {team}
            </Link>
          </div>
          {flags.includes("No seat") ? <MixupFlag>No seat</MixupFlag> : null}
          {flags.includes("Same name") ? <MixupFlag>Same name</MixupFlag> : null}
        </div>
      </DataTableCell>
      <DataTableCell className="py-2.5 text-sm">
        <PriorityMark row={row} />
      </DataTableCell>
      <DataTableCell className="py-2.5">
        {row.admin_label?.trim() ? <LabelMark row={row} /> : faint}
      </DataTableCell>
      <DataTableCell className="py-2.5">
        {row.owner_email ? (
          <span title={row.owner_email} data-testid={`admin-all-teams-owner-${row.team_id}`}>
            {ownerLabel(row)}
          </span>
        ) : (
          faint
        )}
      </DataTableCell>
      <DataTableCell className="py-2.5">
        <LeadsCell leads={leads} noLead={leads.length === 0 || flags.includes("No lead")} />
      </DataTableCell>
      <DataTableCell className="py-2.5">
        <span
          className="whitespace-nowrap tabular-nums"
          title={memberSplit(row.member_counts)}
          data-testid={`admin-all-teams-members-${row.team_id}`}
        >
          {memberWords(row.member_counts)}
        </span>
      </DataTableCell>
      <DataTableCell className="py-2.5">
        <span className="whitespace-nowrap tabular-nums">
          {spanWords(row.latest_cycle_start, row.latest_cycle_end) ?? "None yet"}
        </span>
      </DataTableCell>
      {consoleMode.extraColumns?.map((col) => (
        <DataTableCell key={col.key} className="py-2.5">
          {col.cell(row)}
        </DataTableCell>
      ))}
    </DataTableRow>
  );
}

function TeamRow({ row, withStatus }: { row: AdminAllTeamsRow; withStatus: boolean }) {
  const counts = row.member_counts;
  const cycleWindow =
    row.latest_cycle_start && row.latest_cycle_end
      ? `latest ${row.latest_cycle_start} → ${row.latest_cycle_end}`
      : null;
  return (
    <DataTableRow data-testid={`admin-all-teams-row-${row.team_id}`}>
      <DataTableCell>
        <div className="flex min-w-[10rem] flex-col gap-0.5">
          {/* #2060 — the name opens the team too, so a phone need not
              scroll the table sideways to the Open button. */}
          <Link
            to={teamHome(row)}
            className="font-medium text-fg hover:underline"
            data-testid={`admin-all-teams-name-${row.team_id}`}
          >
            {row.name}
          </Link>
          <code className="text-[10px] text-fg-faint">{row.team_id.slice(0, 8)}…</code>
        </div>
      </DataTableCell>
      {withStatus ? (
        <DataTableCell>
          <TeamWeekStatus teamId={row.team_id} teamKind={row.team_kind} />
        </DataTableCell>
      ) : null}
      <DataTableCell>
        <div className="flex flex-wrap items-center gap-1">
          <span>{teamKindLabel(row.team_kind)}</span>
          {row.is_demo ? <Badge tone="accent">demo</Badge> : null}
          {row.is_ephemeral ? <Badge tone="neutral">session</Badge> : null}
        </div>
      </DataTableCell>
      <DataTableCell>{row.owner_email || faint}</DataTableCell>
      <DataTableCell>
        <span
          className="whitespace-nowrap tabular-nums"
          title={`${counts.has_account} have an account · ${counts.can_sign_in} can sign in on the web · ${counts.placeholder} placeholder · ${counts.invited} invited`}
          data-testid={`admin-all-teams-members-${row.team_id}`}
        >
          {`${counts.has_account} / ${counts.placeholder} / ${counts.invited}`}
        </span>
      </DataTableCell>
      <DataTableCell>
        <div className="flex flex-col gap-0.5 whitespace-nowrap">
          <span className="tabular-nums">{row.cycle_count}</span>
          {cycleWindow ? <span className="text-[10px] text-fg-faint">{cycleWindow}</span> : null}
        </div>
      </DataTableCell>
      <DataTableCell>
        <span className="whitespace-nowrap">{row.published_through ?? faint}</span>
      </DataTableCell>
      <DataTableCell>
        <span className="whitespace-nowrap">{formatDateTime(row.last_solve_at) ?? faint}</span>
      </DataTableCell>
      <DataTableCell>{row.owner_plan}</DataTableCell>
      <DataTableCell>
        <Button asChild size="sm" variant="secondary">
          <Link
            to={teamHome(row)}
            onClick={(e) => e.stopPropagation()}
            data-testid={`admin-all-teams-open-${row.team_id}`}
          >
            Open
          </Link>
        </Button>
      </DataTableCell>
    </DataTableRow>
  );
}

const STATUS_DOT = {
  success: "bg-success",
  warning: "bg-warning",
  neutral: "bg-fg-faint",
} as const;

/**
 * #2060 — one team's week, in the words of the team home's status line
 * (#2048): "Published", "Solved, not published", "No roster yet", plus
 * any unsolved days. A lighter form than the home's: it reads the team's
 * solves list only (one request, the same cached read the team home
 * makes), not the week's roster, so a published week does not say
 * "covered" or "N seats short" here. The read waits until the row is on
 * screen.
 */
export function TeamWeekStatus({ teamId, teamKind }: { teamId: string; teamKind: string }) {
  const { accountId } = useAuth();
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  const isTasks = teamKind === "tasks";
  const cyclesQuery = useQuery({
    queryKey: ["team", teamId, "cycles", accountId],
    enabled: Boolean(visible && !isTasks && teamId && accountId),
    staleTime: 60_000,
    retry: false,
    queryFn: () => apiFetch<HomeCycle[]>(`/api/teams/${teamId}/cycles`),
  });

  if (isTasks) {
    return <span className="text-fg-faint">—</span>;
  }
  const cycles = Array.isArray(cyclesQuery.data) ? cyclesQuery.data : undefined;
  if (!cycles) {
    return (
      <span ref={ref} className="text-xs text-fg-faint" data-testid={`admin-team-status-${teamId}`}>
        {cyclesQuery.isError ? "Couldn't read" : "…"}
      </span>
    );
  }
  const today = todayIso();
  const week = weekOf(today);
  const status = weekStatus(week, today, cycles, undefined);
  // Without the week's roster there is no seat count: a published week
  // with every day solved reads green here.
  const tone =
    status.state === "published" && status.unsolvedDays === 0 ? "success" : weekStatusTone(status);
  return (
    <span
      ref={ref}
      className="flex min-w-[9rem] items-center gap-2 text-sm"
      title={weekLabel(week)}
      data-testid={`admin-team-status-${teamId}`}
      data-state={status.state}
    >
      <span className={cn("h-2 w-2 shrink-0 rounded-full", STATUS_DOT[tone])} aria-hidden="true" />
      <span>{weekStatusWords(status)}</span>
    </span>
  );
}
